/**
 * "Gert á eldri útgáfu nótnanna" (pure, unit tested): layers keep the sheet revision they were drawn
 * on; when the sheet gets a new revision they count as stale until their owner confirms them
 * ("Staðfesta", engine.confirmLayerRevision). The rule itself lives in the engine (LayerView.stale:
 * older revision AND has marks), so the badge, the banner, the review and the hide filter agree.
 */
import type { LayerView } from '../annotations/contract';

type StaleInput = Pick<LayerView, 'id' | 'mine' | 'sheetRev' | 'stale'>;

/**
 * Stale as the engine sees it (LayerView.stale). Only an engine without that field falls back to
 * comparing revisions (a newer sheetRev than the file's means this device is behind, not stale).
 */
export function isStaleLayer(layer: Pick<LayerView, 'sheetRev' | 'stale'>, fileRev: number): boolean {
  return layer.stale ?? layer.sheetRev < fileRev;
}

export interface StaleLayers<L extends StaleInput = LayerView> {
  /** Own stale layers: the owner can review and confirm them. */
  own: L[];
  /** Other people's stale layers: labelled only. */
  shared: L[];
  /** Ids of every stale layer (own and shared). */
  ids: ReadonlySet<string>;
}

export function staleLayers<L extends StaleInput>(layers: readonly L[], fileRev: number): StaleLayers<L> {
  const own: L[] = [];
  const shared: L[] = [];
  const ids = new Set<string>();
  for (const l of layers) {
    if (!isStaleLayer(l, fileRev)) continue;
    (l.mine ? own : shared).push(l);
    ids.add(l.id);
  }
  return { own, shared, ids };
}
